import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, FileText, Receipt, RefreshCw, Bell, Send, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import logoQuickbooks from "../assets/logo-quickbooks.png";
import logoXero from "../assets/logo-xero.png";
import logoGoogleSheets from "../assets/logo-google-sheets.png";
import logoZapier from "../assets/logo-zapier.png";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface ActivityItem {
  id: number;
  eventType: string;
  summary: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface ActivityResponse {
  items: ActivityItem[];
  page: number;
  limit: number;
  hasMore: boolean;
}

interface EventMeta {
  label: string;
  icon?: React.ElementType;
  logoSrc?: string;
  color: string;
  bg: string;
}

const EVENT_META: Record<string, EventMeta> = {
  quote_sent: { label: "Quote Sent", icon: FileText, color: "#2563eb", bg: "#eff6ff" },
  service_agreement_sent: { label: "Quote + Agreement Sent", icon: FileText, color: "#2563eb", bg: "#eff6ff" },
  invoice_sent: { label: "Invoice Sent", icon: Receipt, color: "#7c3aed", bg: "#f5f3ff" },
  payment_recorded: { label: "Payment Recorded", icon: CheckCircle, color: "#16a34a", bg: "#f0fdf4" },
  recurring_generated: { label: "Recurring Generated", icon: RefreshCw, color: "#0891b2", bg: "#ecfeff" },
  reminder_sent: { label: "Reminder Sent", icon: Bell, color: "#d97706", bg: "#fffbeb" },
  email_bounced: { label: "Email Not Delivered", icon: AlertTriangle, color: "#dc2626", bg: "#fef2f2" },
  quickbooks_synced: { label: "QuickBooks Synced", logoSrc: logoQuickbooks, color: "#16a34a", bg: "#f0fdf4" },
  quickbooks_sync_failed: { label: "QuickBooks Failed", logoSrc: logoQuickbooks, color: "#dc2626", bg: "#fef2f2" },
  xero_synced: { label: "Xero Synced", logoSrc: logoXero, color: "#16a34a", bg: "#f0fdf4" },
  xero_sync_failed: { label: "Xero Failed", logoSrc: logoXero, color: "#dc2626", bg: "#fef2f2" },
  sheets_synced: { label: "Sheets Synced", logoSrc: logoGoogleSheets, color: "#16a34a", bg: "#f0fdf4" },
  sheets_sync_failed: { label: "Sheets Failed", logoSrc: logoGoogleSheets, color: "#dc2626", bg: "#fef2f2" },
  zapier_delivered: { label: "Zapier Delivered", logoSrc: logoZapier, color: "#16a34a", bg: "#f0fdf4" },
  zapier_delivery_failed: { label: "Zapier Failed", logoSrc: logoZapier, color: "#dc2626", bg: "#fef2f2" },
};

function getEventMeta(eventType: string): EventMeta {
  return EVENT_META[eventType] ?? { label: eventType.replace(/_/g, " "), icon: Send, color: "#6b7280", bg: "#f9fafb" };
}

async function fetchActivity(page: number): Promise<ActivityResponse> {
  const res = await fetch(`${BASE}/api/activity?page=${page}&limit=25`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch activity log");
  return res.json();
}

export default function ActivityPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["activity", page],
    queryFn: () => fetchActivity(page),
    staleTime: 30_000,
  });

  return (
    <div>
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ marginBottom: "0.5rem" }}>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "hsl(var(--foreground))", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Activity style={{ width: "1.75rem", height: "1.75rem", color: "hsl(var(--primary))" }} />
            Activity Log
          </h1>
        </div>
        <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>
          A chronological record of recent events in your account — emails sent, payments recorded, and automated actions. Showing the last 90 days.
        </p>
      </div>

      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: "4.5rem",
                borderRadius: "0.5rem",
                background: "hsl(var(--muted))",
                opacity: 0.6,
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          ))}
        </div>
      )}

      {isError && (
        <div style={{
          padding: "2rem",
          textAlign: "center",
          border: "1px solid hsl(var(--border))",
          borderRadius: "0.5rem",
          color: "hsl(var(--muted-foreground))",
        }}>
          Failed to load activity log. Please refresh the page.
        </div>
      )}

      {data && data.items.length === 0 && !isLoading && (
        <div style={{
          padding: "3rem 2rem",
          textAlign: "center",
          border: "1px dashed hsl(var(--border))",
          borderRadius: "0.5rem",
          color: "hsl(var(--muted-foreground))",
        }}>
          <Activity style={{ width: "2.5rem", height: "2.5rem", margin: "0 auto 1rem", opacity: 0.4 }} />
          <p style={{ fontWeight: 500, marginBottom: "0.25rem" }}>No activity yet</p>
          <p style={{ fontSize: "0.875rem" }}>
            Events will appear here once you start sending quotes, recording payments, and more.
          </p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <div style={{
          border: "1px solid hsl(var(--border))",
          borderRadius: "0.5rem",
          overflow: "hidden",
          background: "hsl(var(--card))",
        }}>
          {data.items.map((item, idx) => {
            const meta = getEventMeta(item.eventType);
            const Icon = meta.icon;
            const isLast = idx === data.items.length - 1;

            return (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "1rem",
                  padding: "1rem 1.25rem",
                  borderBottom: isLast ? "none" : "1px solid hsl(var(--border))",
                }}
              >
                <div style={{
                  width: "2.25rem",
                  height: "2.25rem",
                  borderRadius: "50%",
                  background: meta.logoSrc ? "#ffffff" : meta.bg,
                  border: meta.logoSrc ? "1px solid hsl(var(--border))" : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                }}>
                  {meta.logoSrc ? (
                    <img
                      src={meta.logoSrc}
                      alt=""
                      style={{ width: "1.375rem", height: "1.375rem", objectFit: "contain" }}
                    />
                  ) : Icon ? (
                    <Icon style={{ width: "1rem", height: "1rem", color: meta.color }} />
                  ) : null}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.125rem" }}>
                    <span style={{
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: meta.color,
                      background: meta.bg,
                      padding: "0.1rem 0.45rem",
                      borderRadius: "9999px",
                    }}>
                      {meta.label}
                    </span>
                  </div>
                  <p style={{ fontSize: "0.875rem", color: "hsl(var(--foreground))", margin: 0, lineHeight: 1.4 }}>
                    {item.summary}
                  </p>
                </div>

                <div style={{
                  fontSize: "0.75rem",
                  color: "hsl(var(--muted-foreground))",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  paddingTop: "0.125rem",
                }}>
                  {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data && (data.hasMore || page > 1) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "1rem" }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.375rem",
              padding: "0.5rem 0.875rem",
              border: "1px solid hsl(var(--border))",
              borderRadius: "0.375rem",
              background: "hsl(var(--card))",
              color: page === 1 ? "hsl(var(--muted-foreground))" : "hsl(var(--foreground))",
              cursor: page === 1 ? "not-allowed" : "pointer",
              fontSize: "0.875rem",
              opacity: page === 1 ? 0.5 : 1,
            }}
          >
            <ChevronLeft style={{ width: "1rem", height: "1rem" }} />
            Previous
          </button>

          <span style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>
            Page {page}
          </span>

          <button
            onClick={() => setPage(p => p + 1)}
            disabled={!data.hasMore}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.375rem",
              padding: "0.5rem 0.875rem",
              border: "1px solid hsl(var(--border))",
              borderRadius: "0.375rem",
              background: "hsl(var(--card))",
              color: !data.hasMore ? "hsl(var(--muted-foreground))" : "hsl(var(--foreground))",
              cursor: !data.hasMore ? "not-allowed" : "pointer",
              fontSize: "0.875rem",
              opacity: !data.hasMore ? 0.5 : 1,
            }}
          >
            Next
            <ChevronRight style={{ width: "1rem", height: "1rem" }} />
          </button>
        </div>
      )}
    </div>
  );
}
